/**
 * FeedbackFAB — Always-visible Floating Action Button for user feedback
 * Renders a fixed FAB at bottom-right that opens a feedback dialog
 */

import React, { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  TouchApp as UxIcon,
  MoreHoriz as OtherIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

interface FeedbackFABProps {
  onSubmit: (feedback: {
    category: string;
    description: string;
    pageUrl: string;
  }) => Promise<void>;
}

/**
 * Map a submission error to a user-facing message in German.
 * Covers axios-shaped network/HTTP errors plus already-thrown Error instances.
 */
export function feedbackErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as { code?: string; response?: { status?: number } };
    if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') {
      return 'Zeitueberschreitung — Feedback konnte nicht gesendet werden. Bitte erneut versuchen.';
    }
    if (e.response?.status === 401) {
      return 'Anmeldung abgelaufen — bitte neu anmelden.';
    }
    if (e.response?.status === 403) {
      return 'Keine Berechtigung zum Senden von Feedback.';
    }
    if (e.code === 'ERR_NETWORK' || (e.response === undefined && e.code !== undefined)) {
      return 'Netzwerkfehler — bitte Verbindung pruefen.';
    }
  }
  if (err instanceof Error && err.message) {
    return `Fehler beim Senden des Feedbacks: ${err.message}`;
  }
  return 'Fehler beim Senden des Feedbacks';
}

const categories = [
  { value: 'bug', label: 'Bug', icon: <BugIcon fontSize="small" /> },
  { value: 'feature', label: 'Feature', icon: <FeatureIcon fontSize="small" /> },
  { value: 'ux', label: 'UX', icon: <UxIcon fontSize="small" /> },
  { value: 'other', label: 'Sonstiges', icon: <OtherIcon fontSize="small" /> },
];

export default function FeedbackFAB({ onSubmit }: FeedbackFABProps) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });
  const location = useLocation();

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (!submitting) {
      setOpen(false);
      setCategory('');
      setDescription('');
    }
  };

  const handleSubmit = async () => {
    if (!category || description.trim().length < 10) return;

    setSubmitting(true);
    try {
      await onSubmit({
        category,
        description: description.trim(),
        // Backend Zod schema (SEC #348/F2) requires a full http(s) URL as
        // defense-in-depth against javascript:/data: URLs once pageUrl is
        // rendered as <a href>. location.pathname alone would fail validation.
        pageUrl: window.location.href,
      });
      setSnackbar({ open: true, severity: 'success', message: 'Feedback gesendet — vielen Dank!' });
      handleClose();
    } catch (err: unknown) {
      setSnackbar({ open: true, severity: 'error', message: feedbackErrorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = category && description.trim().length >= 10;

  return (
    <>
      <Fab
        color="primary"
        aria-label="Feedback geben"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
        }}
      >
        <FeedbackIcon />
      </Fab>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Feedback geben</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Hilf uns, die Anwendung zu verbessern. Dein Feedback wird direkt an das Entwicklungsteam weitergeleitet.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Kategorie
          </Typography>
          <ToggleButtonGroup
            value={category}
            exclusive
            onChange={(_, val) => val && setCategory(val)}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            {categories.map((cat) => (
              <ToggleButton key={cat.value} value={cat.value} sx={{ gap: 0.5 }}>
                {cat.icon}
                {cat.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            label="Beschreibung"
            placeholder="Was moechtest du mitteilen? (min. 10 Zeichen)"
            multiline
            rows={4}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText={`${description.length} / min. 10 Zeichen`}
            error={description.length > 0 && description.length < 10}
            sx={{ mt: 1 }}
          />

          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Seite: {location.pathname}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isValid || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {submitting ? 'Sende...' : 'Feedback senden'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
